import { Fragment } from 'react';
import { useRouter } from 'next/router';
import Head from 'next/head';
import { useQuery } from '@apollo/client';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';
import _map from 'lodash/map';
import _get from 'lodash/get';
import Loading from '../../../components/loading';
import ErrorMessage from '../../../components/error-message';
import NotFound from '../../../components/not-found';
import Markdown from '../../../components/markdown';
import { cityQuery } from '../../../apollo/cities';
import { buildTitle } from '../../../utils/layout';
import config from '../../../config';
import Main from '../../../components/main';
import MaterialLink from '../../../components/material-link';
import { useAppContext } from '../../../contexts/app';
import MaterialLinkButton from '../../../components/material-link-button';
import PrimaryToggle from '../../../components/cities/primary-toggle';

const useStyles = makeStyles((theme) => ({
  headerWrapper: {
    marginTop: theme.spacing(3),
    display: 'flex',
    alignItems: 'center',
    '& > *:first-child': {
      flexGrow: 1,
    },
  },
  header: {
    margin: 0,
  },
  locality: {
    display: 'block',
  },
  iframe: {
    width: '100%',
    height: 500,
    border: 'none',
    backgroundColor: '#ccc'
  },
}));

const buildJsonLDBreadcrumbs = (city) => {
  const items = [
    {
      '@id': `${config().universal.baseUri}/cities`,
      name: 'Cities',
    },
    {
      '@id': `${config().universal.baseUri}/cities/${city.slug}`,
      name: city.name,
    },
  ];
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: _map(items, (item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      item
    }))
  };
};

const City = () => {
  const router = useRouter();
  const classes = useStyles();
  const { city: appCity } = useAppContext();

  const { data: cityData, loading, error } = useQuery(cityQuery, {
    variables: {
      slug: router.query.citySlug,
    },
  });
  const city = _get(cityData, 'city');
  const territories = _get(city, 'territoriesJson') ? JSON.parse(_get(city, 'territoriesJson')) : null;

  if (error) {
    return <ErrorMessage message='An error occurred while loading city.' error={error} />;
  }
  if (loading) { return <Loading />; }
  if (!city) { return <NotFound />; }

  const metaDescription = `About ${city.name}, ${city.locality}`;
  const canonicalUrl = `${config().universal.baseUri}/cities/${city.slug}`;

  return (
    <Main>
      <Head>
        <title>{buildTitle(city.name)}</title>
        <link rel="canonical" href={canonicalUrl} />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(buildJsonLDBreadcrumbs(city))
          }}
        />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:title" content={city.name} />
        <meta property="og:description" content={metaDescription} />
        <meta name="description" content={metaDescription} />
      </Head>

      <div className={classes.headerWrapper}>
        <div>
          <Typography variant="h1" className={classes.header}>{city.name}</Typography>
          <div className={classes.locality}>{city.locality}</div>
        </div>
        <div>
          {_get(appCity, 'slug') === router.query.citySlug ? (
            <MaterialLinkButton
              href="/calendar"
              variant="contained"
              color="primary"
            >
              View Calendar
            </MaterialLinkButton>
          ) : (
            <PrimaryToggle city={city} />
          )}
        </div>
      </div>

      <Markdown>{city.description}</Markdown>

      <Typography variant="h2">Land Acknowledgement</Typography>
      <p>
        {city.name} is located on the stolen lands of the peoples of{' '}
        <Fragment>
          {_map(territories, (territory, index) => (
            <Fragment>
              {index > 0 && ', '}
              {index === territories.length - 1 && 'and '}
              <MaterialLink href={territory.properties.description}>
                {territory.properties.Name}
              </MaterialLink>
            </Fragment>
          ))}
        </Fragment>
        {'.'}
      </p>
      <iframe
        className={classes.iframe}
        src={`https://native-land.ca/api/embed/embed.html?maps=territories&position=${city.latitude},${city.longitude}`}
      ></iframe>
      <p>Maps and territories data provided by <MaterialLink href="https://native-land.ca/">native-land.ca</MaterialLink></p>

      {city.resources && (
        <Fragment>
          <Typography variant="h2">Resources</Typography>
          <Markdown>{city.resources}</Markdown>
        </Fragment>
      )}
    </Main>
  );
};

export default City;
